import React, { useCallback, useEffect } from "react"
import { useState } from "react"
import { Button } from "../../../components/ui/button"
import { Badge } from "../../../components/ui/badge"
import { Input } from "../../../components/ui/input"
import { Card, CardContent, CardFooter, CardHeader } from "../../../components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select"
import {
  Search,
  Plus,
  Bell,
  Grid,
  List,
  Eye,
  Mail,
  Calendar,
  Building,
  MoreVertical,
  ArrowUpDown,
  CheckCircle2,
  XCircle,
  Clock,
  ViewIcon,
  RefreshCwIcon,
  Loader2
} from "lucide-react"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../../components/ui/dropdown-menu"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table"
import { Avatar, AvatarFallback, AvatarImage } from "../../../components/ui/avatar"
import { PartnerGridList } from "./PartnerGridList";
import { PartnerTableList } from "./PartnerTableList";
import CommonModal from "../Modals/CommonModal";
import PartnerDetailsView from "./PartnerDetailsView";
import { useInvitationManagement } from "../../Services/InvitationManagement";
import { useUserContext } from "../../../Contexts/UserProvider";
import { usePartnerManagement } from "../../Services/PartnerManagement";
import { useHasPermission } from "../../../components/hooks/useHasPermission"
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import InvitePartner from "./InvitePartner";
import ConfirmDialog from "../Modals/ConfirmationModal"

// Helper function to get status badge
const getStatusBadge = (status: string) => {
  switch (status) {
    case "Accept":
      return (
        <Badge className="bg-green-100 text-green-800 flex items-center gap-1">
          <CheckCircle2 className="h-3 w-3" />
          Accepted
        </Badge>
      )
    case "Canceled":
      return (
        <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200 flex items-center gap-1">
          <XCircle className="h-3 w-3" />
          Canceled
        </Badge>
      )
    case "Pending":
      return (
        <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-200 flex items-center gap-1">
          <Clock className="h-3 w-3" />
          Pending
        </Badge>
      )
    default:
      return (
        <Badge variant="outline" className="bg-gray-100 text-gray-800 flex items-center gap-1">
          {status}
        </Badge>
      )
  }
}

// Helper function to format date
const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

// Helper function to get partnership type badge
const getPartnershipTypeBadge = (type: string) => {
  switch (type) {
    case "Partner":
      return <Badge className="bg-purple-100 text-purple-800">Partner</Badge>
    case "ReSeller":
      return <Badge className="bg-blue-100 text-blue-800">ReSeller</Badge>
    default:
      return <Badge className="bg-gray-100 text-gray-800">{type}</Badge>
  }
}

// Helper function to get company logo or initials
const getCompanyLogo = (name: string, logo?: string) => {
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .substring(0, 2)
    .toUpperCase()

  return (
    <Avatar className="h-10 w-10 border">
      <AvatarImage
        src={logo ? `/placeholder.svg?height=40&width=40&text=${encodeURIComponent(initials)}` : ""}
        alt={name}
      />
      <AvatarFallback className="bg-purple-100 text-purple-800">{initials}</AvatarFallback>
    </Avatar>
  )
}

const Partners = () => {
  const [searchQuery, setSearchQuery] = useState("")
  const [viewMode, setViewMode] = useState<"grid" | "list">("list")
  const [statusFilter, setStatusFilter] = useState("All Status")
  const [typeFilter, setTypeFilter] = useState("All Types")

  const [isShow, setIsShow] = useState(false);
  const [partners, setPartners] = useState([]);
  const { getPartners } = usePartnerManagement();
  const { resendInvitation } = useInvitationManagement();
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(null);
  const [isCompanyDetails, setIsCompanyDetails] = useState({
    isShow: false,
    data: null
  });
  const [confirmationModal, setConfirmationModal] = useState({
    isShow: false,
    onConfirm: () => { },
  });
  const { tenant } = useUserContext();
  const navigate = useNavigate();
  const { id } = useParams();

  const canCreatePartnership = useHasPermission("RoleManagement.Create");

  const handleInviteUser = useCallback(() => {
    setIsShow((prev) => !prev);
  }, []);

  useEffect(() => {
    getPartnerDetails();
  }, [id]);

  const getPartnerDetails = async () => {
    setIsLoading(true);
    try {
      const response: any = await getPartners(
        "isTenantManagement"
      );
      setPartners(response);
    } catch (error) {
      console.error("Error fetching partners: ", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendInvitation = useCallback(
    async (id) => {
      try {
        await resendInvitation(id);
        toast.success("Invitation resent successfully!");
        getPartnerDetails();
      } catch (error) {
        toast.error("Failed to resend the invitation. Please try again.");
        console.error("Error resending invitation: ", error);
      }
    },
    [resendInvitation]
  );

  // Filter partnerships based on search query, status, and type
  const filteredPartnerships = partners.filter(
    (partnership) =>
      (partnership.invitedTo.toLowerCase().includes(searchQuery.toLowerCase()) ||
        partnership.invitedFromTenantName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        partnership.userEmail.toLowerCase().includes(searchQuery.toLowerCase())) &&
      (statusFilter === "All Status" || partnership.notificationStatus === statusFilter) &&
      (typeFilter === "All Types" || partnership.userType === typeFilter),
  )

  // Get unique statuses and types for filters
  const statuses = ["All Status", ...Array.from(new Set(partners.map((partnership) => partnership.notificationStatus)))]
  const types = ["All Types", ...Array.from(new Set(partners.map((partnership) => partnership.userType)))]

  const handleNavigateToCatalogue = (data) => {
    navigate(`/app/${id}/products/${data?.invitedToTenantId}?name=${encodeURIComponent(data?.invitedToTenantName)}`);
  }

  const handleDetailsView = (data, isShow) => {
    setIsDropdownOpen(null);
    setIsCompanyDetails((prev) => ({
      data: data !== undefined && data !== null ? data : prev.data,
      isShow: isShow
    }));
  };

  const handleConfirmationModal = (isOpen, onConfirm) => {
    setConfirmationModal({
      isShow: isOpen,
      onConfirm: onConfirm,
    });
  };

  return (
    <div className="w-full mx-auto p-6 space-y-6 company-list">
      <main className="container mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Companies & Partnerships</h2>
          {canCreatePartnership && (
            <Button className="bg-primary hover:bg-[#8e44ad] text-white" onClick={handleInviteUser}>
              <Plus className="h-4 w-4 mr-2" /> Add Partnerships
            </Button>
          )}
        </div>

        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search companies, partners or emails..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          {/* <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              {statuses.map((status) => (
                <SelectItem key={status} value={status}>
                  {status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select> */}
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              {types.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex gap-2">
            <Button
              variant={viewMode === "grid" ? "default" : "outline"}
              className={viewMode === "grid" ? "bg-primary hover:bg-[#8e44ad]" : ""}
              onClick={() => setViewMode("grid")}
            >
              <Grid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "list" ? "default" : "outline"}
              className={viewMode === "list" ? "bg-primary hover:bg-[#8e44ad]" : ""}
              onClick={() => setViewMode("list")}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <Tabs defaultValue="all" className="w-full mb-6">
          <TabsList>
            <TabsTrigger value="all">All Partnerships</TabsTrigger>
            <TabsTrigger value="accepted">Accepted</TabsTrigger>
            <TabsTrigger value="pending">Pending</TabsTrigger>
            <TabsTrigger value="canceled">Canceled</TabsTrigger>
          </TabsList>

                    <TabsContent value="all">
            {isLoading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="animate-spin h-5 w-5 text-gray-500" />
              </div>
            ) : viewMode === "grid" ? (
              <PartnerGridList 
                partnerships={filteredPartnerships}
                tenant={tenant}
                getCompanyLogo={getCompanyLogo}
                handleDetailsView={handleDetailsView}
                handleNavigateToCatalogue={handleNavigateToCatalogue}
                handleConfirmationModal={handleConfirmationModal}
                handleResendInvitation={handleResendInvitation}
                getStatusBadge={getStatusBadge}
                formatDate={formatDate}
                isDropdownOpen={isDropdownOpen}
                setIsDropdownOpen={setIsDropdownOpen}
              />
            ) : (
              <PartnerTableList 
                partnerships={filteredPartnerships}
                tenant={tenant}
                getCompanyLogo={getCompanyLogo}
                getPartnershipTypeBadge={getPartnershipTypeBadge}
                getStatusBadge={getStatusBadge}
                formatDate={formatDate}
                handleDetailsView={handleDetailsView}
                handleNavigateToCatalogue={handleNavigateToCatalogue}
                handleConfirmationModal={handleConfirmationModal}
                handleResendInvitation={handleResendInvitation}
                isDropdownOpen={isDropdownOpen}
                setIsDropdownOpen={setIsDropdownOpen}
              />
            )}
          </TabsContent>

          <TabsContent value="accepted">
            {isLoading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="animate-spin h-5 w-5 text-gray-500" />
              </div>
            ) : viewMode === "grid" ? (
              <PartnerGridList 
                partnerships={filteredPartnerships.filter((p) => p.notificationStatus === "Accept")}
                tenant={tenant}
                getCompanyLogo={getCompanyLogo}
                handleDetailsView={handleDetailsView}
                handleNavigateToCatalogue={handleNavigateToCatalogue}
                handleConfirmationModal={handleConfirmationModal}
                handleResendInvitation={handleResendInvitation}
                getStatusBadge={getStatusBadge}
                formatDate={formatDate}
                isDropdownOpen={isDropdownOpen}
                setIsDropdownOpen={setIsDropdownOpen}
              />
            ) : (
              <PartnerTableList 
                partnerships={filteredPartnerships.filter((p) => p.notificationStatus === "Accept")}
                tenant={tenant}
                getCompanyLogo={getCompanyLogo}
                getPartnershipTypeBadge={getPartnershipTypeBadge}
                getStatusBadge={getStatusBadge}
                formatDate={formatDate}
                handleDetailsView={handleDetailsView}
                handleNavigateToCatalogue={handleNavigateToCatalogue}
                handleConfirmationModal={handleConfirmationModal}
                handleResendInvitation={handleResendInvitation}
                isDropdownOpen={isDropdownOpen}
                setIsDropdownOpen={setIsDropdownOpen}
              />
            )}
          </TabsContent>

          <TabsContent value="pending">
            {isLoading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="animate-spin h-5 w-5 text-gray-500" />
              </div>
            ) : viewMode === "grid" ? (
              <PartnerGridList 
                partnerships={filteredPartnerships.filter((p) => p.notificationStatus === "Pending")}
                tenant={tenant}
                getCompanyLogo={getCompanyLogo}
                handleDetailsView={handleDetailsView}
                handleNavigateToCatalogue={handleNavigateToCatalogue}
                handleConfirmationModal={handleConfirmationModal}
                handleResendInvitation={handleResendInvitation}
                getStatusBadge={getStatusBadge}
                formatDate={formatDate}
                isDropdownOpen={isDropdownOpen}
                setIsDropdownOpen={setIsDropdownOpen}
              />
            ) : (
              <PartnerTableList 
                partnerships={filteredPartnerships.filter((p) => p.notificationStatus === "Pending")}
                tenant={tenant}
                getCompanyLogo={getCompanyLogo}
                getPartnershipTypeBadge={getPartnershipTypeBadge}
                getStatusBadge={getStatusBadge}
                formatDate={formatDate}
                handleDetailsView={handleDetailsView}
                handleNavigateToCatalogue={handleNavigateToCatalogue}
                handleConfirmationModal={handleConfirmationModal}
                handleResendInvitation={handleResendInvitation}
                isDropdownOpen={isDropdownOpen}
                setIsDropdownOpen={setIsDropdownOpen}
              />
            )}
          </TabsContent>

          <TabsContent value="canceled">
            {isLoading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="animate-spin h-5 w-5 text-gray-500" />
              </div>
            ) : viewMode === "grid" ? (
              <PartnerGridList 
                partnerships={filteredPartnerships.filter((p) => p.notificationStatus === "Canceled")}
                tenant={tenant}
                getCompanyLogo={getCompanyLogo}
                handleDetailsView={handleDetailsView}
                handleNavigateToCatalogue={handleNavigateToCatalogue}
                handleConfirmationModal={handleConfirmationModal}
                handleResendInvitation={handleResendInvitation}
                getStatusBadge={getStatusBadge}
                formatDate={formatDate}
                isDropdownOpen={isDropdownOpen}
                setIsDropdownOpen={setIsDropdownOpen}
              />
            ) : (
              <PartnerTableList 
                partnerships={filteredPartnerships.filter((p) => p.notificationStatus === "Canceled")}
                tenant={tenant}
                getCompanyLogo={getCompanyLogo}
                getPartnershipTypeBadge={getPartnershipTypeBadge}
                getStatusBadge={getStatusBadge}
                formatDate={formatDate}
                handleDetailsView={handleDetailsView}
                handleNavigateToCatalogue={handleNavigateToCatalogue}
                handleConfirmationModal={handleConfirmationModal}
                handleResendInvitation={handleResendInvitation}
                isDropdownOpen={isDropdownOpen}
                setIsDropdownOpen={setIsDropdownOpen}
              />
            )}
          </TabsContent>
        </Tabs>
      </main>

      <InvitePartner isShow={isShow} setIsShow={setIsShow} successCallBack={getPartnerDetails} />
      <ConfirmDialog isShow={confirmationModal?.isShow} setIsDialogOpen={setConfirmationModal} title="Are you sure to continue ?" description="" confirmText="Confirm" cancelText="Cancel" onConfirm={confirmationModal?.onConfirm} />
      <PartnerDetailsView record={isCompanyDetails?.data} open={isCompanyDetails?.isShow} onOpenChange={(isOpen) => handleDetailsView(null, isOpen)} />
    </div>
  )
}

export default Partners;

